import React from "react";
import { getSignedUrl } from "graphQL/query/file";
import { useImageUpload } from "../contexts/ImageUploadContext";

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export function useMultiplayerAssets(roomId: string) {
  const { startUpload, endUpload } = useImageUpload();

  const onAssetCreate = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (file: File, id: string): Promise<string | false> => {
      try {
        startUpload();

        // Upload original file without compression to preserve full quality for print
        const fileToUpload = file;

        // For small images (< 100KB), use base64 to avoid S3 upload overhead
        if (fileToUpload.size < 100000) {
          try {
            const base64 = await toBase64(fileToUpload);
            return base64 as string;
          } catch (err) {
            console.error("Error converting to base64:", err);
            // Fallback to S3 if base64 fails
          }
        }

        // For large images (>= 100KB), upload to S3
        const filename = encodeURIComponent(file.name).replace(/[^a-zA-Z0-9 ]/g, "");
        const encodedRoomId = encodeURIComponent(roomId).replace(/[^a-zA-Z0-9 ]/g, "");
        const key = `campaign-builder/fb-images/${encodedRoomId}/${filename}id${new Date().getTime()}`;
        try {
          const signedUrlPUT = await getSignedUrl("oppizi-uploads", key, "PUT", "ap-southeast-2");

          // Start upload and GET URL request in parallel to save time
          const [_, signedUrlGET] = await Promise.all([
            fetch(signedUrlPUT, {
              method: "PUT",
              body: fileToUpload,
              headers: {
                "Content-Type": fileToUpload.type,
              },
            }),
            getSignedUrl("oppizi-uploads", key, "GET", "ap-southeast-2"),
          ]);

          // Add a special marker in the URL fragment to store the S3 key for regeneration
          const urlWithKey = `${signedUrlGET}#s3key=${encodeURIComponent(key)}`;
          return urlWithKey;
        } catch (err) {
          console.error("Error uploading to S3:", err);
          throw err;
        }
      } finally {
        endUpload();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startUpload, endUpload]
  );

  const onAssetDelete = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (id: string): Promise<boolean> => {
      // noop
      return true;
    },
    []
  );

  return { onAssetCreate, onAssetDelete };
}
